import './index-filters.scss';
import Base from '../base/base';
import { debounce } from '../../shared/utils/helpers';
import { BREAKPOINTS } from '../../shared/utils/variables';

/**
 * A dropdown filter: matches items whose attribute equals the selected value
 * (compared case/punctuation-insensitively).
 */
interface SelectFilterDef {
  type: 'select';
  /** Item data attribute the filter reads, e.g. 'data-neighborhood' */
  attribute: string;
  /** Accessible name for the control; shown as a visible label in the sheet */
  label: string;
  /** Label of the match-everything option, default 'All' */
  allLabel?: string;
  /**
   * Fixed option list, displayed in the given order. When omitted, options
   * are derived from the values present in the DOM (sorted alphabetically).
   * Use this for known taxonomies (e.g. statuses) so every option shows even
   * when no item currently carries it.
   */
  options?: string[];
  /**
   * Value to preselect at init, matched hyphen- and case-insensitively
   * ("east-village" selects "East Village"); an unmatched value is ignored.
   */
  initial?: string;
  /**
   * 'prefix' also matches values the active value starts ("the-kombi"
   * catches "the-kombi-coupe"), hyphen-insensitively. Default 'exact'.
   */
  match?: 'exact' | 'prefix';
  /**
   * Render no control: the filter acts only through `initial` (e.g. a URL
   * param), and only the URL can clear it.
   */
  hidden?: boolean;
}

/**
 * A text search filter: matches items whose attribute contains every
 * whitespace-separated token of the query (case/punctuation-insensitive).
 */
interface SearchFilterDef {
  type: 'search';
  /** Item data attribute the filter reads, e.g. 'data-full-address' */
  attribute: string;
  /** Accessible name for the control */
  label: string;
  /** Input placeholder text */
  placeholder?: string;
}

/**
 * One entry in a sort dropdown. `type` picks the comparator: 'text' compares
 * the raw attribute with localeCompare, 'number' compares the digits-only
 * value ("$359,000" -> 359000, "1,644 sq ft" -> 1644). Items whose value is
 * missing or unparseable sort to the bottom in BOTH directions.
 */
export interface SortOption {
  /** Visible text in the dropdown, e.g. 'Price - Low to High' */
  label: string;
  /** Item data attribute this option orders by */
  attribute: string;
  /** Comparator to use */
  type: 'text' | 'number';
  /** 'asc' (A-Z / low-high) or 'desc' (Z-A / high-low) */
  direction: 'asc' | 'desc';
}

/**
 * A sort dropdown: reorders the collection in place rather than hiding
 * anything. Renders as a select, identical in markup to a select filter, so
 * the two are styled by the same rules. Unlike select filters it never
 * relocates into the sheet — a single control fits the bar at any width.
 */
interface SortControlDef {
  type: 'sort';
  /** Accessible name for the control */
  label: string;
  /** Resting option, which restores the original order. Default 'Sort By'. */
  restingLabel?: string;
  /** The sort options, in dropdown order */
  options: SortOption[];
}

export type FilterDef = SelectFilterDef | SearchFilterDef | SortControlDef;

/**
 * Configuration options for IndexFilters
 */
interface IndexFiltersOptions {
  /** Selector for the wrapper around the collection */
  wrapSelector?: string;
  /** Selector for the filterable items, scoped to the wrapper */
  itemSelector?: string;
  /** Items matching this selector are ignored (e.g. Webflow ghost/spacer items) */
  excludeSelector?: string;
  /** Selector for the container the filter controls render into */
  containerSelector?: string;
  /**
   * Selector for the descendant of an item carrying the data attributes. When
   * omitted, attributes are read from the item itself. Use it when the
   * filtered/reordered element and the data-bearing element differ (e.g.
   * '.nbrhood-item' wrapping a '.kl-hcard').
   */
  dataSelector?: string;
  /** The filters to render, in order. Matching ANDs across all of them. */
  filters?: FilterDef[];
  /** Message shown when no items match */
  emptyMessage?: string;
  /** Debounce for text inputs in ms */
  searchDebounce?: number;
  /** Render an "N results" line above the controls row */
  showCount?: boolean;
  /**
   * Hide the export's page heading in the stacked (narrow) layout, where the
   * active page-switch pill already names the page. Styling lives in
   * kl-index.scss, which owns the stacking breakpoint.
   */
  hideHeadingOnMobile?: boolean;
  /**
   * Selector of a fixed page header (e.g. '.nav') to stick beneath. When set,
   * the controls container becomes position: sticky with the header's
   * measured height as its top offset (re-measured on resize).
   */
  stickyUnder?: string;
  /**
   * Viewport width (px) at or below which the select filters relocate into
   * the full-screen sheet behind the trigger button. Defaults to
   * BREAKPOINTS.mobile; pass a larger breakpoint (e.g. BREAKPOINTS.tablet)
   * when the bar shares the row with other content and needs the room.
   */
  sheetMaxWidth?: number;
  /**
   * Selector of an element to constrain the open sheet to (its horizontal box,
   * pinned from beneath stickyUnder to the viewport bottom) instead of letting
   * it cover the full screen. Only applies above BREAKPOINTS.mobile — below
   * that the sheet always goes full-screen. Use it to keep adjacent content
   * (e.g. a map column) visible while the sheet is open. Default unset.
   */
  sheetBounds?: string;
  /** false keeps the selects in the bar at every width. Default true. */
  sheet?: boolean;
  /** Sheetless layout: 'row' side by side, 'stacked' full width. */
  inlineLayout?: 'row' | 'stacked';
  /** Render a removable chip for each active hidden filter. Default true. */
  chips?: boolean;
  /** Called with the matching items whenever the filtered set changes */
  onChange?: (matching: HTMLElement[]) => void;
  /** Enable debug logging */
  debug?: boolean;
  [key: string]: any;
}

/**
 * Cached per-item attribute values: raw for display, normalized for matching,
 * numeric for sorting (null when nothing parseable remains).
 */
interface CachedValue {
  raw: string;
  norm: string;
  num: number | null;
}

/** Resolved configuration (options with defaults applied) */
interface IndexFiltersConfig {
  wrapSelector: string;
  itemSelector: string;
  excludeSelector: string;
  containerSelector: string;
  dataSelector: string;
  filters: FilterDef[];
  emptyMessage: string;
  searchDebounce: number;
  showCount: boolean;
  hideHeadingOnMobile: boolean;
  stickyUnder: string;
  sheetMaxWidth: number;
  sheetBounds: string;
  sheet: boolean;
  inlineLayout: 'row' | 'stacked';
  chips: boolean;
  onChange?: (matching: HTMLElement[]) => void;
}

const FUNNEL_ICON =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M1.5 2h13l-5 6v5l-3 1.5V8l-5-6z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

const CLOSE_ICON =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

/** Unique-id counter for select/label association */
let selectUid = 0;

/**
 * IndexFilters
 *
 * Renders controls (dropdowns, text search, sort) into a container and applies
 * them to a server-rendered (Webflow CMS) collection list via the items' data
 * attributes. Filters hide non-matching items with a class; a sort reorders
 * the items in place and hides nothing. Either way the resulting set is
 * reported through `onChange`, in its current order, so a paginator (e.g.
 * IndexPagination) can re-window it.
 *
 * A sort control renders the same markup as a select filter but carries
 * `.cf-field--sort`, because it stays in the bar at every width rather than
 * relocating into the sheet. On a page whose only control is a sort, no sheet
 * or trigger is built at all.
 *
 * Layout: the container holds a one-line controls bar (`.cf-bar`) and an
 * optional count line above it (`.cf-count`). With `stickyUnder`, the whole
 * container sticks beneath the site's fixed header while the list scrolls.
 *
 * Sheet mode (viewport ≤ sheetMaxWidth, default BREAKPOINTS.mobile): the
 * select fields relocate into a full-screen sheet opened by a trigger button
 * in the bar — the same DOM nodes move between bar and sheet, so filter state
 * needs no syncing. The container carries SHEET_MODE_CLASS while the
 * breakpoint matches, and the stylesheet keys off that class rather than a
 * media query, so the breakpoint stays configurable per instance. The
 * search input always stays in the bar. Filters apply live; the sheet's
 * primary button ("Show N homes") just closes it.
 *
 * Attribute values are read once at init into a cache (raw + normalized) —
 * predicates never touch the DOM per keystroke, and future value cleanup
 * (e.g. parsing '$445,000' price strings) belongs in that cache layer.
 */
export default class IndexFilters extends Base {
  /** Class applied to items hidden because they don't match the active filters */
  static readonly HIDDEN_ITEM_CLASS = 'cf-item-hidden';
  /** Class applied to the controls container while controls are rendered */
  static readonly CONTAINER_CLASS = 'cf';
  /** Class applied to the controls container when stickyUnder is configured */
  static readonly STICKY_CLASS = 'cf--sticky';
  /** Class applied to the controls container while the sheet breakpoint matches */
  static readonly SHEET_MODE_CLASS = 'cf--sheet';
  /** Class applied to the sheet element while it is constrained to sheetBounds */
  static readonly SHEET_SCOPED_CLASS = 'cf-sheet--scoped';
  /** Class applied to the controls container when hideHeadingOnMobile is set */
  static readonly HIDE_HEADING_CLASS = 'cf--hide-heading';
  /** Class applied to the container when sheetless selects stack */
  static readonly INLINE_STACKED_CLASS = 'cf--inline-stacked';

  private config: IndexFiltersConfig;

  // DOM references
  private wrap: HTMLElement | null = null;
  private items: HTMLElement[] = [];
  private containerEl: HTMLElement | null = null;
  private barEl: HTMLElement | null = null;
  private countEl: HTMLElement | null = null;
  private chipsEl: HTMLElement | null = null;
  private emptyEl: HTMLElement | null = null;
  private selectFields: HTMLElement[] = [];
  private triggerEl: HTMLButtonElement | null = null;
  private badgeEl: HTMLElement | null = null;
  private sheetEl: HTMLElement | null = null;
  private sheetBodyEl: HTMLElement | null = null;
  private closeButtonEl: HTMLButtonElement | null = null;
  private applyButtonEl: HTMLButtonElement | null = null;
  private controls: { element: HTMLElement; event: string; handler: EventListener }[] = [];

  // State
  private valueCache = new Map<HTMLElement, Record<string, CachedValue>>();
  private activeValues = new Map<FilterDef, string>();
  /** Items in their original DOM order, so the resting sort option can restore it */
  private originalOrder: HTMLElement[] = [];
  private activeSort: SortOption | null = null;
  /** Sort dropdowns, kept apart so "Clear All Filters" leaves them untouched */
  private sortSelects: HTMLSelectElement[] = [];
  private destroyed = false;
  private sheetOpen = false;
  private prevBodyOverflow = '';
  private sheetQuery: MediaQueryList | null = null;
  private placementHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private stickyResizeObserver: ResizeObserver | null = null;
  private boundsEl: HTMLElement | null = null;
  private sheetResizeHandler: (() => void) | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(options: IndexFiltersOptions = {}) {
    super(options.debug || false);

    this.config = {
      wrapSelector: '.home-collection-wrap',
      itemSelector: '.ml-item',
      excludeSelector: '.ml--ghost',
      containerSelector: '.map-filters',
      dataSelector: '',
      filters: [],
      emptyMessage: 'No homes match your search. Try adjusting your filters.',
      searchDebounce: 150,
      showCount: false,
      hideHeadingOnMobile: true,
      stickyUnder: '',
      sheetMaxWidth: BREAKPOINTS.mobile,
      sheetBounds: '',
      sheet: true,
      inlineLayout: 'row',
      chips: true,
      ...options
    };

    this.init();
  }

  init(): void {
    this.wrap = document.querySelector<HTMLElement>(this.config.wrapSelector);

    if (!this.wrap) {
      this.log('Wrapper not found, skipping initialization', this.config.wrapSelector);
      return;
    }

    if (!this.config.filters.length) {
      this.log('No filter definitions provided, skipping initialization');
      return;
    }

    this.items = Array.from(
      this.wrap.querySelectorAll<HTMLElement>(this.config.itemSelector)
    ).filter((item) => !this.config.excludeSelector || !item.matches(this.config.excludeSelector));

    // Snapshot before any sort reorders `items`, so the resting option can return to it
    this.originalOrder = this.items.slice();

    this.buildValueCache();

    this.containerEl = this.resolveContainer();
    this.buildControls();

    if (this.config.sheetBounds) {
      this.boundsEl = document.querySelector<HTMLElement>(this.config.sheetBounds);
      if (!this.boundsEl) this.log('Sheet bounds element not found', this.config.sheetBounds);
    }

    this.initSticky();
    this.initResponsivePlacement();

    // An initial value starts a filter active; otherwise the full set matches
    if (this.activeValues.size) this.applyFilters();
    else this.updateDerivedUI(this.items.length);

    this.log(`Initialized with ${this.config.filters.length} filter(s) over ${this.items.length} item(s)`);
  }

  /**
   * The set of items matching the currently active filters.
   */
  getMatchingItems(): HTMLElement[] {
    return this.items.filter((item) => this.matches(item));
  }

  /* ===================
     Matching
     =================== */

  /**
   * Read every attribute the filter defs need, once, into the cache.
   * Future value normalization/cleanup (e.g. price strings) lives here.
   */
  private buildValueCache(): void {
    // A sort def carries no attribute of its own — each of its options names one
    const attributes = Array.from(
      new Set(
        this.config.filters.flatMap((def) =>
          def.type === 'sort' ? def.options.map((option) => option.attribute) : [def.attribute]
        )
      )
    );

    this.items.forEach((item) => {
      // The data may live on a descendant (dataSelector) while the item itself
      // stays the hide/reorder target
      const source = this.config.dataSelector
        ? item.querySelector<HTMLElement>(this.config.dataSelector) || item
        : item;

      const values: Record<string, CachedValue> = {};
      attributes.forEach((attribute) => {
        const raw = (source.getAttribute(attribute) || '').trim();
        values[attribute] = {
          raw,
          norm: IndexFilters.normalize(raw),
          num: IndexFilters.toNumber(raw)
        };
      });
      this.valueCache.set(item, values);
    });
  }

  /** Digits-only integer, or null when nothing parseable remains */
  private static toNumber(value: string): number | null {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits) return null;
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Lowercase, strip light punctuation, collapse whitespace — so
   * "st detroit" matches "St. Detroit".
   */
  private static normalize(value: string): string {
    return value
      .toLowerCase()
      .replace(/[.,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** normalize, with hyphens as spaces — a URL slug equals its display name */
  private static fold(value: string): string {
    return IndexFilters.normalize(value.replace(/-/g, ' '));
  }

  private cachedValue(item: HTMLElement, attribute: string): CachedValue {
    return this.valueCache.get(item)?.[attribute] || { raw: '', norm: '', num: null };
  }

  private matches(item: HTMLElement): boolean {
    return this.config.filters.every((def) => {
      // Sorting reorders; it never narrows the set
      if (def.type === 'sort') return true;

      const active = this.activeValues.get(def) || '';
      if (!active) return true;

      const value = this.cachedValue(item, def.attribute);

      if (def.type === 'select') {
        if (def.match === 'prefix') {
          return IndexFilters.fold(value.raw).startsWith(IndexFilters.fold(active));
        }
        // Normalized equality, so CMS casing drift ("sold" vs "Sold") matches
        return value.norm === IndexFilters.normalize(active);
      }

      // search: every token of the query must appear in the normalized value
      const tokens = IndexFilters.normalize(active).split(' ').filter(Boolean);
      return tokens.every((token) => value.norm.includes(token));
    });
  }

  /**
   * Apply the active filters: hide non-matching items, toggle the empty
   * message, refresh the count/badge/apply texts, and report the matching set.
   */
  private applyFilters(): void {
    if (this.destroyed) return;

    const matching: HTMLElement[] = [];

    this.items.forEach((item) => {
      const isMatch = this.matches(item);
      item.classList.toggle(IndexFilters.HIDDEN_ITEM_CLASS, !isMatch);
      if (isMatch) matching.push(item);
    });

    this.toggleEmptyMessage(matching.length === 0);
    this.updateDerivedUI(matching.length);
    this.log(`${matching.length} of ${this.items.length} item(s) match`);

    this.config.onChange?.(matching);
  }

  /* ===================
     Controls
     =================== */

  /**
   * Find the controls container, or create one before the wrapper so the
   * component still works before the Webflow export ships the dedicated div.
   */
  private resolveContainer(): HTMLElement {
    const existing = document.querySelector<HTMLElement>(this.config.containerSelector);
    if (existing) return existing;

    this.log('Filters container not found, creating one', this.config.containerSelector);
    const container = document.createElement('div');
    const selector = this.config.containerSelector;
    if (selector.startsWith('#')) {
      container.id = selector.slice(1);
    } else {
      container.className = selector.replace(/^\./, '');
    }
    this.wrap!.insertAdjacentElement('beforebegin', container);
    return container;
  }

  private buildControls(): void {
    if (!this.containerEl) return;

    this.containerEl.classList.add(IndexFilters.CONTAINER_CLASS);
    this.containerEl.classList.toggle(
      IndexFilters.HIDE_HEADING_CLASS,
      this.config.hideHeadingOnMobile
    );
    this.containerEl.classList.toggle(
      IndexFilters.INLINE_STACKED_CLASS,
      !this.config.sheet && this.config.inlineLayout === 'stacked'
    );

    // The export ships the page heading (h1.map-header) inside this container,
    // with the right text per page. Append to it rather than clearing it, so
    // Webflow stays the single source of that copy.

    // Single-line controls bar: fields in def order, sheet trigger last
    this.barEl = document.createElement('div');
    this.barEl.className = 'cf-bar';
    this.selectFields = [];
    this.sortSelects = [];

    this.config.filters.forEach((def) => {
      if (def.type === 'select') {
        if (def.hidden) {
          this.seedInitial(def, this.selectValues(def));
          return;
        }
        const field = this.buildSelectField(def);
        // Only select filters relocate into the sheet
        this.selectFields.push(field);
        this.barEl!.appendChild(field);
      } else if (def.type === 'sort') {
        this.barEl!.appendChild(this.buildSortField(def));
      } else {
        this.barEl!.appendChild(this.buildSearchField(def));
      }
    });

    // No select filters (a sort-only page), or sheet off: no trigger.
    if (this.config.sheet && this.selectFields.length) {
      this.barEl.appendChild(this.buildSheetTrigger());
    }

    // Count line above the bar (inside the sticky container, so always visible)
    if (this.config.showCount) {
      this.countEl = document.createElement('p');
      this.countEl.className = 'cf-count';
      this.countEl.setAttribute('aria-live', 'polite');
      this.containerEl.appendChild(this.countEl);
    }

    this.containerEl.appendChild(this.barEl);
    this.buildChips();
    if (this.config.sheet && this.selectFields.length) this.buildSheet();
  }

  /**
   * One removable chip per active hidden filter, on a row of its own —
   * the visitor's only way to clear a filter that renders no control.
   */
  private buildChips(): void {
    if (!this.config.chips || !this.containerEl) return;

    const active = this.config.filters.filter(
      (def): def is SelectFilterDef =>
        def.type === 'select' && !!def.hidden && !!this.activeValues.get(def)
    );

    if (!active.length) return;

    this.chipsEl = document.createElement('div');
    this.chipsEl.className = 'cf-chips';

    active.forEach((def) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cf-chip';
      chip.setAttribute('aria-label', `Clear filter ${def.label}: ${this.activeValues.get(def)}`);

      const text = document.createElement('span');
      text.textContent = `${def.label}: ${this.activeValues.get(def)}`;
      chip.appendChild(text);
      chip.insertAdjacentHTML('beforeend', CLOSE_ICON);

      const handler = () => {
        this.activeValues.set(def, '');
        chip.remove();
        if (this.chipsEl && !this.chipsEl.childElementCount) {
          this.chipsEl.remove();
          this.chipsEl = null;
        }
        this.applyFilters();
      };
      chip.addEventListener('click', handler);
      this.controls.push({ element: chip, event: 'click', handler });

      this.chipsEl!.appendChild(chip);
    });

    this.containerEl.appendChild(this.chipsEl);
  }

  private buildSelectField(def: SelectFilterDef): HTMLElement {
    const field = document.createElement('div');
    field.className = 'cf-field cf-field--select';

    const select = document.createElement('select');
    select.className = 'cf-select';
    select.id = `cf-select-${selectUid++}`;
    select.setAttribute('aria-label', def.label);

    // Visible label (shown only while the field sits in the sheet)
    const label = document.createElement('label');
    label.className = 'cf-label';
    label.htmlFor = select.id;
    label.textContent = def.label;

    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = def.allLabel || 'All';
    select.appendChild(allOption);

    const values = this.selectValues(def);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });

    const match = this.seedInitial(def, values);
    if (match) select.value = match;

    const handler = () => {
      this.activeValues.set(def, select.value);
      this.applyFilters();
    };
    select.addEventListener('change', handler);
    this.controls.push({ element: select, event: 'change', handler });

    field.appendChild(label);
    field.appendChild(select);
    return field;
  }

  /** The option values of a select filter: the fixed list, else the DOM's. */
  private selectValues(def: SelectFilterDef): string[] {
    return def.options?.length ? def.options : this.distinctValues(def.attribute);
  }

  /**
   * Activate `def.initial` (matched hyphen- and case-insensitively against
   * `values`) and return the match, so a visible control can mirror it.
   */
  private seedInitial(def: SelectFilterDef, values: string[]): string | null {
    if (!def.initial) return null;

    const wanted = IndexFilters.fold(def.initial);
    // A prefix initial activates as itself — no single option carries it
    const match =
      def.match === 'prefix'
        ? values.some((value) => IndexFilters.fold(value).startsWith(wanted))
          ? def.initial
          : undefined
        : values.find((value) => IndexFilters.fold(value) === wanted);

    if (!match) {
      this.log('Initial value matches no option', def.attribute, def.initial);
      return null;
    }

    this.activeValues.set(def, match);
    return match;
  }

  /**
   * Same markup as buildSelectField, so both controls pick up identical
   * styling. Option values are indices into def.options; '-1' is the resting
   * option, which restores the original order.
   */
  private buildSortField(def: SortControlDef): HTMLElement {
    const field = document.createElement('div');
    // Its own modifier rather than --select: a sort field is never relocated
    // into the sheet, so it must not pick up the rules that hide relocated
    // selects, and it caps its own width instead of filling the bar.
    field.className = 'cf-field cf-field--sort';

    const select = document.createElement('select');
    select.className = 'cf-select';
    select.id = `cf-select-${selectUid++}`;
    select.setAttribute('aria-label', def.label);

    const label = document.createElement('label');
    label.className = 'cf-label';
    label.htmlFor = select.id;
    label.textContent = def.label;

    const resting = document.createElement('option');
    resting.value = '-1';
    resting.textContent = def.restingLabel || 'Sort By';
    select.appendChild(resting);

    def.options.forEach((option, index) => {
      const el = document.createElement('option');
      el.value = String(index);
      el.textContent = option.label;
      select.appendChild(el);
    });

    const handler = () => {
      const index = Number(select.value);
      this.activeSort = def.options[index] || null;
      this.applySort();
      // Re-run matching so the reported set carries the new order
      this.applyFilters();
    };
    select.addEventListener('change', handler);
    this.controls.push({ element: select, event: 'change', handler });
    this.sortSelects.push(select);

    field.appendChild(label);
    field.appendChild(select);
    return field;
  }

  /* ===================
     Sorting
     =================== */

  /** Reorder `items` and the DOM to the active sort, or back to the original */
  private applySort(): void {
    this.items = this.activeSort
      ? this.items.slice().sort((a, b) => this.compareBySort(a, b, this.activeSort!))
      : this.originalOrder.slice();

    this.reorderDom(this.items);
    this.log(this.activeSort ? `Sorted by "${this.activeSort.label}"` : 'Restored original order');
  }

  /** Missing or unparseable values sort last, whichever the direction */
  private compareBySort(a: HTMLElement, b: HTMLElement, option: SortOption): number {
    if (option.type === 'number') {
      const av = this.cachedValue(a, option.attribute).num;
      const bv = this.cachedValue(b, option.attribute).num;

      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;

      return option.direction === 'asc' ? av - bv : bv - av;
    }

    const av = this.cachedValue(a, option.attribute).raw;
    const bv = this.cachedValue(b, option.attribute).raw;

    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;

    const result = av.localeCompare(bv, undefined, { sensitivity: 'base' });
    return option.direction === 'asc' ? result : -result;
  }

  /** Move the item nodes into `ordered` order under their shared parent */
  private reorderDom(ordered: HTMLElement[]): void {
    const parent = ordered[0]?.parentElement;
    if (!parent) return;
    ordered.forEach((item) => parent.appendChild(item));
  }

  private buildSearchField(def: SearchFilterDef): HTMLElement {
    const field = document.createElement('div');
    field.className = 'cf-field cf-field--search';

    const input = document.createElement('input');
    input.type = 'search';
    input.className = 'cf-input';
    input.setAttribute('aria-label', def.label);
    if (def.placeholder) input.placeholder = def.placeholder;

    const apply = debounce(() => {
      this.activeValues.set(def, input.value);
      this.applyFilters();
    }, this.config.searchDebounce);

    const handler = () => apply();
    input.addEventListener('input', handler);
    this.controls.push({ element: input, event: 'input', handler });

    field.appendChild(input);
    return field;
  }

  /**
   * Distinct attribute values for a derived select, deduped
   * case-insensitively (first-seen raw value wins for display).
   */
  private distinctValues(attribute: string): string[] {
    const byNorm = new Map<string, string>();
    this.items.forEach((item) => {
      const { raw, norm } = this.cachedValue(item, attribute);
      if (raw && !byNorm.has(norm)) byNorm.set(norm, raw);
    });
    return Array.from(byNorm.values()).sort((a, b) => a.localeCompare(b));
  }

  private toggleEmptyMessage(show: boolean): void {
    if (show && !this.emptyEl) {
      this.emptyEl = document.createElement('p');
      this.emptyEl.className = 'cf-empty';
      this.emptyEl.textContent = this.config.emptyMessage;
      this.wrap!.appendChild(this.emptyEl);
    }

    if (this.emptyEl) {
      this.emptyEl.hidden = !show;
    }
  }

  /** Refresh the texts derived from the matching set: count, badge, apply button */
  private updateDerivedUI(matchCount: number): void {
    if (this.countEl) {
      this.countEl.textContent = `${matchCount} ${matchCount === 1 ? 'result' : 'results'}`;
    }

    if (this.applyButtonEl) {
      this.applyButtonEl.textContent = `Show ${matchCount} ${matchCount === 1 ? 'home' : 'homes'}`;
    }

    if (this.badgeEl) {
      const active = this.activeSheetFilterCount();
      this.badgeEl.textContent = String(active);
      this.badgeEl.hidden = active === 0;
    }
  }

  /** Number of active select filters — the ones hidden inside the sheet on mobile */
  private activeSheetFilterCount(): number {
    let count = 0;
    this.activeValues.forEach((value, def) => {
      if (def.type === 'select' && value) count += 1;
    });
    return count;
  }

  /* ===================
     Sticky bar
     =================== */

  private initSticky(): void {
    if (!this.config.stickyUnder || !this.containerEl) return;

    this.containerEl.classList.add(IndexFilters.STICKY_CLASS);
    this.measureStickyOffset();

    // The header's height can animate without a viewport resize (e.g. the
    // nav compacting on scroll), so track it directly — a one-shot measure
    // would leave the bar pinned at a stale offset.
    const header = document.querySelector<HTMLElement>(this.config.stickyUnder);
    if (header && typeof ResizeObserver !== 'undefined') {
      this.stickyResizeObserver = new ResizeObserver(() => this.measureStickyOffset());
      this.stickyResizeObserver.observe(header);
    }

    this.resizeHandler = debounce(() => this.measureStickyOffset(), 150);
    window.addEventListener('resize', this.resizeHandler);
  }

  /** Measure the fixed header so the bar pins exactly beneath it */
  private measureStickyOffset(): void {
    if (!this.containerEl) return;
    const header = document.querySelector<HTMLElement>(this.config.stickyUnder);
    const offset = header ? header.offsetHeight : 0;
    this.containerEl.style.setProperty('--cf-sticky-top', `${offset}px`);
    this.log('Sticky offset measured', offset);
  }

  /* ===================
     Mobile filter sheet
     =================== */

  private buildSheetTrigger(): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cf-sheet-trigger';
    button.setAttribute('aria-label', 'Open filters');
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = FUNNEL_ICON;

    this.badgeEl = document.createElement('span');
    this.badgeEl.className = 'cf-sheet-trigger__badge';
    this.badgeEl.hidden = true;
    button.appendChild(this.badgeEl);

    const handler = () => this.openSheet();
    button.addEventListener('click', handler);
    this.controls.push({ element: button, event: 'click', handler });

    this.triggerEl = button;
    return button;
  }

  private buildSheet(): void {
    const sheet = document.createElement('div');
    sheet.className = 'cf-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Filters');

    const header = document.createElement('div');
    header.className = 'cf-sheet__header';

    const title = document.createElement('h2');
    title.className = 'cf-sheet__title';
    title.textContent = 'Filters';
    header.appendChild(title);

    this.closeButtonEl = document.createElement('button');
    this.closeButtonEl.type = 'button';
    this.closeButtonEl.className = 'cf-sheet__close';
    this.closeButtonEl.setAttribute('aria-label', 'Close filters');
    this.closeButtonEl.innerHTML = CLOSE_ICON;
    const closeHandler = () => this.closeSheet();
    this.closeButtonEl.addEventListener('click', closeHandler);
    this.controls.push({ element: this.closeButtonEl, event: 'click', handler: closeHandler });
    header.appendChild(this.closeButtonEl);

    this.sheetBodyEl = document.createElement('div');
    this.sheetBodyEl.className = 'cf-sheet__body';

    const footer = document.createElement('div');
    footer.className = 'cf-sheet__footer';

    const clearButton = document.createElement('button');
    clearButton.type = 'button';
    clearButton.className = 'cf-sheet__clear';
    clearButton.textContent = 'Clear All Filters';
    const clearHandler = () => this.clearAllFilters();
    clearButton.addEventListener('click', clearHandler);
    this.controls.push({ element: clearButton, event: 'click', handler: clearHandler });
    footer.appendChild(clearButton);

    this.applyButtonEl = document.createElement('button');
    this.applyButtonEl.type = 'button';
    this.applyButtonEl.className = 'cf-sheet__apply';
    const applyHandler = () => this.closeSheet();
    this.applyButtonEl.addEventListener('click', applyHandler);
    this.controls.push({ element: this.applyButtonEl, event: 'click', handler: applyHandler });
    footer.appendChild(this.applyButtonEl);

    sheet.appendChild(header);
    sheet.appendChild(this.sheetBodyEl);
    sheet.appendChild(footer);

    // Body-level so the fixed overlay isn't trapped by the sticky container
    document.body.appendChild(sheet);
    this.sheetEl = sheet;
  }

  /**
   * Track the sheet breakpoint and move the select fields between the bar
   * (wide viewports) and the sheet body (at or below sheetMaxWidth). The same
   * elements move, so the selected values carry over with no state syncing.
   */
  private initResponsivePlacement(): void {
    if (!this.config.sheet) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return; // bar placement (the build order) is already correct
    }

    this.sheetQuery = window.matchMedia(`(max-width: ${this.config.sheetMaxWidth}px)`);
    this.placementHandler = () => {
      if (!this.sheetQuery!.matches && this.sheetOpen) this.closeSheet(false);
      this.syncPlacement(this.sheetQuery!.matches);
    };
    this.sheetQuery.addEventListener('change', this.placementHandler);

    this.syncPlacement(this.sheetQuery.matches);
  }

  private syncPlacement(inSheet: boolean): void {
    if (!this.barEl || !this.sheetBodyEl) return;

    // The stylesheet keys the trigger's visibility and the bar layout off
    // this class, so they follow the configured breakpoint.
    this.containerEl?.classList.toggle(IndexFilters.SHEET_MODE_CLASS, inSheet);

    // Note: restoring to the bar re-inserts select fields (in def order)
    // just before the trigger; non-select fields never move.
    this.selectFields.forEach((field) => {
      if (inSheet) {
        this.sheetBodyEl!.appendChild(field);
      } else {
        this.barEl!.insertBefore(field, this.triggerEl);
      }
    });

    this.log(`Select fields placed in ${inSheet ? 'sheet' : 'bar'}`);
  }

  private openSheet(): void {
    if (!this.sheetEl || this.sheetOpen) return;

    this.sheetOpen = true;
    // Size the box (scoped to bounds, or full-screen) before revealing it
    this.applySheetBounds();
    this.sheetEl.classList.add('is-open');
    this.triggerEl?.setAttribute('aria-expanded', 'true');

    // Lock page scroll behind the sheet. The bounds box is viewport-pinned, so
    // locking scroll also keeps it stable without tracking scroll position.
    this.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    this.keydownHandler = (event: KeyboardEvent) => this.handleSheetKeydown(event);
    document.addEventListener('keydown', this.keydownHandler);

    // Re-measure (or fall back to full-screen below the mobile breakpoint) when
    // the viewport changes while the sheet is open.
    if (this.config.sheetBounds) {
      this.sheetResizeHandler = debounce(() => this.applySheetBounds(), 100);
      window.addEventListener('resize', this.sheetResizeHandler);
    }

    this.closeButtonEl?.focus();
    this.log('Sheet opened');
  }

  /**
   * Constrain the open sheet to the sheetBounds element's horizontal box —
   * pinned from beneath the sticky header to the viewport bottom — so adjacent
   * content (e.g. the map) stays visible. Only above BREAKPOINTS.mobile; below
   * that (or with no bounds) the sheet covers the full screen.
   */
  private applySheetBounds(): void {
    if (!this.sheetEl) return;

    const scope =
      !!this.boundsEl &&
      typeof window !== 'undefined' &&
      window.innerWidth > BREAKPOINTS.mobile;

    if (!scope) {
      this.clearSheetBounds();
      return;
    }

    const rect = this.boundsEl!.getBoundingClientRect();
    const top = this.navOffset();

    this.sheetEl.classList.add(IndexFilters.SHEET_SCOPED_CLASS);
    this.sheetEl.style.left = `${rect.left}px`;
    this.sheetEl.style.width = `${rect.width}px`;
    this.sheetEl.style.top = `${top}px`;
    this.log('Sheet scoped to bounds', { left: rect.left, width: rect.width, top });
  }

  /**
   * Height of the fixed site header, so the sheet stops short of covering it.
   * Prefers the shared `--nav-h` published on :root by IndexViewToggle,
   * which means the sheet positions correctly without this instance having to
   * configure `stickyUnder` at all. Falls back to measuring `stickyUnder` for
   * pages that set it and run no view toggle.
   */
  private navOffset(): number {
    const shared = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--nav-h')
    );
    if (Number.isFinite(shared) && shared > 0) return shared;

    const header = this.config.stickyUnder
      ? document.querySelector<HTMLElement>(this.config.stickyUnder)
      : null;
    return header ? header.offsetHeight : 0;
  }

  /** Reset the sheet to its default full-screen box */
  private clearSheetBounds(): void {
    if (!this.sheetEl) return;
    this.sheetEl.classList.remove(IndexFilters.SHEET_SCOPED_CLASS);
    this.sheetEl.style.left = '';
    this.sheetEl.style.width = '';
    this.sheetEl.style.top = '';
  }

  private closeSheet(restoreFocus: boolean = true): void {
    if (!this.sheetEl || !this.sheetOpen) return;

    this.sheetOpen = false;
    this.sheetEl.classList.remove('is-open');
    this.triggerEl?.setAttribute('aria-expanded', 'false');

    document.body.style.overflow = this.prevBodyOverflow;

    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    if (this.sheetResizeHandler) {
      window.removeEventListener('resize', this.sheetResizeHandler);
      this.sheetResizeHandler = null;
    }
    this.clearSheetBounds();

    if (restoreFocus) this.triggerEl?.focus();
    this.log('Sheet closed');
  }

  /** Escape closes; Tab cycles within the sheet (it covers the whole page) */
  private handleSheetKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSheet();
      return;
    }

    if (event.key !== 'Tab' || !this.sheetEl) return;

    const focusables = Array.from(
      this.sheetEl.querySelectorAll<HTMLElement>('button, select, input, [href]')
    ).filter((el) => !el.hasAttribute('disabled'));
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Reset every filter control and re-apply. The sort control is deliberately
   * left alone: clearing filters widens the set, it does not reorder it.
   */
  private clearAllFilters(): void {
    this.activeValues.clear();

    this.controls.forEach(({ element }) => {
      if (this.sortSelects.includes(element as HTMLSelectElement)) return;
      if (element instanceof HTMLSelectElement || element instanceof HTMLInputElement) {
        element.value = '';
      }
    });

    this.applyFilters();
    this.log('All filters cleared');
  }

  /* ===================
     Cleanup
     =================== */

  /**
   * Cleanup method: removes controls and the sheet, unhooks listeners, and
   * restores all items to visible.
   */
  destroy(): void {
    this.destroyed = true;

    if (this.sheetOpen) this.closeSheet(false);

    this.controls.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.controls = [];

    if (this.sheetQuery && this.placementHandler) {
      this.sheetQuery.removeEventListener('change', this.placementHandler);
    }
    this.sheetQuery = null;
    this.placementHandler = null;

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    this.stickyResizeObserver?.disconnect();
    this.stickyResizeObserver = null;

    if (this.sheetResizeHandler) {
      window.removeEventListener('resize', this.sheetResizeHandler);
      this.sheetResizeHandler = null;
    }
    this.boundsEl = null;

    this.sheetEl?.remove();
    this.sheetEl = null;
    this.sheetBodyEl = null;
    this.closeButtonEl = null;
    this.applyButtonEl = null;
    this.triggerEl = null;
    this.badgeEl = null;
    this.selectFields = [];
    this.sortSelects = [];

    // Remove only what this component added — the container also holds the
    // export's h1.map-header, which must survive teardown
    this.barEl?.remove();
    this.countEl?.remove();
    this.chipsEl?.remove();

    if (this.containerEl) {
      this.containerEl.classList.remove(
        IndexFilters.CONTAINER_CLASS,
        IndexFilters.STICKY_CLASS,
        IndexFilters.SHEET_MODE_CLASS,
        IndexFilters.HIDE_HEADING_CLASS
      );
      this.containerEl.style.removeProperty('--cf-sticky-top');
    }
    this.barEl = null;
    this.countEl = null;
    this.chipsEl = null;

    this.emptyEl?.remove();
    this.emptyEl = null;

    this.items.forEach((item) => item.classList.remove(IndexFilters.HIDDEN_ITEM_CLASS));

    // Leave the collection in the order it was found in
    this.reorderDom(this.originalOrder);
    this.items = this.originalOrder.slice();
    this.activeSort = null;

    this.valueCache.clear();
    this.activeValues.clear();
  }
}
